import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../axiosInstance/axiosInstance";
import { ProjectResponseMapper } from "./AccessUserProjectResponseMapper";
import { UserProjectAccessResponse } from "./UserProjectAccessResponse";
import { UserProjectAccess } from "./UserProjectAccess";
import { ObjectState } from "../ObjectState";
import { RootState } from "../../app/store";
import { AvailableProjectResponse } from "./AvailableProjectResponse";
import { RemoveProjectAccessRequest } from "./RemoveProjectAccessRequest";

export const fetchUserProjectAccesses = createAsyncThunk(
  "userProjectAccess/fetchUserProjectAccesses",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<UserProjectAccessResponse[]>(
        "/api/userprojectaccess"
      );

      const data = response.data;
      const projectResponseMapper = new ProjectResponseMapper();
      const userProjectAccess = data.map((access) =>
        projectResponseMapper.mapAccessUserProjectResponse(access)
      );
      return userProjectAccess;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  }
);

export const fetchProjectToAccess = createAsyncThunk(
  "userProjectAccess/fetchAvailableProjects",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<AvailableProjectResponse[]>(
        "/api/projects/access-projects"
      );

      const data = response.data;
      const projectResponseMapper = new ProjectResponseMapper();
      const projectToAccessResponses = data.map((access) =>
        projectResponseMapper.mapAvailableProjectResponse(access)
      );
      return projectToAccessResponses;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  }
);

export const saveAccessUserProjects = createAsyncThunk(
  "userProjectAccess/saveAccessUserProjects",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    try {
      const userProjectAccesses = state.userProjectAccess.userProjectAccesses;
      const allProjectAccessToDelete =
        selectAllDeletedProjectAccesses(userProjectAccesses);
      const isCompleteDeleteRequests =
        await removeUserAccessProjectsRequestAsync(allProjectAccessToDelete);

      const allProjectAccessToAdd =
        selectAllAddedProjectAccesses(userProjectAccesses);
      const isCompleteAddRequest = await addUserAccessProjectsRequestAsync(
        allProjectAccessToAdd
      );
      if (isCompleteAddRequest && isCompleteDeleteRequests) {
        await thunkApi.dispatch(fetchUserProjectAccesses());
        await thunkApi.dispatch(fetchProjectToAccess());
        return true;
      }
      return false;
    } catch (err) {
      return thunkApi.rejectWithValue(err);
    }
  }
);

const selectAllDeletedProjectAccesses = (
  userProjectAccesses: UserProjectAccess[]
): RemoveProjectAccessRequest[] => {
  const deletedAccessRequests: RemoveProjectAccessRequest[] = [];

  userProjectAccesses.forEach((userAccess) => {
    const deletedProjectIds = userAccess.projectsToAccess
      .filter((project) => project.objectState === ObjectState.Deleted)
      .map((project) => project.id);

    if (deletedProjectIds.length > 0) {
      deletedAccessRequests.push({
        userId: userAccess.userId,
        projectIds: deletedProjectIds,
      });
    }
  });

  return deletedAccessRequests;
};

const removeUserAccessProjectsRequestAsync = async (
  removeProjectAccessRequests: RemoveProjectAccessRequest[]
) => {
  try {
    const removeRequests = removeProjectAccessRequests.map(
      async (bodyRequest) => {
        return axiosInstance.delete(`/api/userprojectaccess`, {
          data: bodyRequest,
        });
      }
    );
    const responses = await Promise.all(removeRequests);

    if (responses.every((response) => response.status === 200)) {
      return true;
    }
    return false;
  } catch (error) {
    throw error;
  }
};

const selectAllAddedProjectAccesses = (
  userProjectAccesses: UserProjectAccess[]
): UserProjectAccess[] => {
  return userProjectAccesses
    .map((userProjectAccess) => ({
      ...userProjectAccess,
      projectsToAccess: userProjectAccess.projectsToAccess.filter(
        (project) => project.objectState === ObjectState.Added
      ),
    }))
    .filter(
      (userProjectAccess) => userProjectAccess.projectsToAccess.length > 0
    );
};

const addUserAccessProjectsRequestAsync = async (
  userProjectAccesses: UserProjectAccess[]
) => {
  try {
    if (userProjectAccesses.length === 0) {
      return true;
    }
    var requestBody = userProjectAccesses.flatMap((u) =>
      u.projectsToAccess.map((p) => ({
        userId: u.userId,
        projectId: p.id,
      }))
    );

    if (requestBody.length === 0) {
      return true;
    }
    const response = await axiosInstance.post(
      `/api/userprojectaccess`,
      requestBody
    );

    return response.status === 200;
  } catch (error) {
    throw error;
  }
};
